import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { graphql, useStaticQuery } from "gatsby";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import classNames from "classnames";
import { useCookies } from "react-cookie";
import Select from "react-select";

import RichTextBlock from "./RichTextBlock";
import InvisibleReCAPTCHA from "./InvisibleReCAPTCHA";
import MobileStoreButton from "./MobileStoreButton";
import DisclaimerCopy from "./DisclaimerCopy";

import { useSiteMetadata } from "../hooks/useSiteMetadata";
import { ATTRIBUTION_COOKIE_NAME } from "./MarketingAttributionStore";

import {
    getFormattedPhoneNumber,
    isValidEmail,
    isValidPhoneNumber,
    isValidName,
} from "../utils/utils";

import "../styles/components/TextMeTheAppForm.scss";

const TextMeTheAppForm = ({ prefix, lead, successMessage, variant }) => {
    const {
        contentfulLicenseTypes: { types },
    } = useStaticQuery(graphql`
        query LicenseTypesQuery {
            contentfulLicenseTypes {
                types
            }
        }
    `);
    const {
        endpoints: { lead: endpointUrl },
    } = useSiteMetadata();

    const [cookies] = useCookies([ATTRIBUTION_COOKIE_NAME]);
    const attribution = cookies.attribution || {};
    const [phoneNumber, setPhoneNumber] = useState("");
    const [name, setName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [tcpa, setTcpa] = useState(false);
    const [licenseTypeOptions, setLicenseTypeOptions] = useState([]);
    const [licensesSelected, setLicensesSelected] = useState([]);
    const [invalidName, setInvalidName] = useState(false);
    const [invalidLastName, setInvalidLastName] = useState(false);
    const [invalidEmail, setInvalidEmail] = useState(false);
    const [invalidPhoneNo, setInvalidPhoneNo] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submissionError, setSubmissionError] = useState(null);

    const postFormData = async reCAPTCHAToken => {
        const formData = {
            phone: phoneNumber,
            first_name: name,
            last_name: lastName,
            certificates_types: licensesSelected?.map(type => type.value),
            recaptcha_token: reCAPTCHAToken,
            email,
            tcpa: +tcpa,
            ...attribution,
        };
        const response = await fetch(endpointUrl, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
        });
        return response.json();
    };

    const submitForm = reCAPTCHAToken => {
        setSubmitting(true);
        const validPhone = isValidPhoneNumber(phoneNumber);
        const validName = isValidName(name);
        const validLastName = isValidName(lastName);
        const validEmail = isValidEmail(email);

        if (
            reCAPTCHAToken &&
            validPhone &&
            (variant !== "extended" ||
                (validName && validLastName && validEmail))
        ) {
            postFormData(reCAPTCHAToken)
                .then(data => {
                    setSubmissionError(data.error ? data.msg : null);
                    setSubmitted(true);
                })
                .catch(error => console.error(error));
        } else {
            setInvalidPhoneNo(!validPhone);
            setInvalidName(!validName);
            setInvalidLastName(!validLastName);
            setInvalidEmail(!validEmail);
        }
        reCAPTCHARef.current.reset();
        setSubmitting(false);
    };

    const updatePhoneNumber = e => {
        let number = getFormattedPhoneNumber(e.target.value);
        setPhoneNumber(number);
        if (invalidPhoneNo) {
            setInvalidPhoneNo(!isValidPhoneNumber(number));
        }
    };

    const updateName = e => {
        setName(e.target.value);
        if (invalidName) {
            setInvalidName(!isValidName(name));
        }
    };

    const updateLastName = e => {
        setLastName(e.target.value);
        if (invalidName) {
            setInvalidLastName(!isValidName(lastName));
        }
    };

    const updateEmail = e => {
        setEmail(e.target.value);
        if (invalidEmail) {
            setInvalidEmail(!isValidEmail(email));
        }
    };

    const updateLicensesTypes = selectedTypes => {
        setLicensesSelected(selectedTypes);
    };

    const updateTcpa = e => {
        setTcpa(e.target.checked);
    };

    const reCAPTCHARef = useRef(null);

    const executeReCAPTCHA = async e => {
        e.preventDefault();
        const token = await reCAPTCHARef.current.executeAsync();
        submitForm(token);
    };

    useEffect(() => {
        const options = types?.map(type => ({
            value: type.toLowerCase(),
            label: type,
        }));

        setLicenseTypeOptions(options);
    }, [types]);

    if (submitted && phoneNumber && !submissionError) {
        return (
            <Alert variant="success">
                {successMessage ? (
                    <RichTextBlock content={successMessage} />
                ) : (
                    `An SMS with a link to the app has been sent to ${phoneNumber}.`
                )}
            </Alert>
        );
    }

    return (
        <Form
            className={classNames("text-me-the-app-form", variant)}
            onSubmit={executeReCAPTCHA}
        >
            {lead && (
                <div className="form-lead">
                    <RichTextBlock content={lead} />
                </div>
            )}
            {variant === "extended" && (
                <Row className="form-submit-container align-items-center">
                    <Col md={6} className="mb-2">
                        <Form.Group controlId={`${prefix}.textMeTheApp.name`}>
                            <Form.Control
                                type="text"
                                size="lg"
                                required
                                value={name}
                                onChange={updateName}
                                placeholder="First Name"
                                className={classNames({ invalid: invalidName })}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-2">
                        <Form.Group
                            controlId={`${prefix}.textMeTheApp.lastName`}
                        >
                            <Form.Control
                                type="text"
                                size="lg"
                                required
                                value={lastName}
                                onChange={updateLastName}
                                placeholder="Last Name"
                                className={classNames({
                                    invalid: invalidLastName,
                                })}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} className="mb-2">
                        <Form.Group controlId={`${prefix}.textMeTheApp.email`}>
                            <Form.Control
                                type="email"
                                size="lg"
                                required
                                value={email}
                                onChange={updateEmail}
                                placeholder="Email"
                                className={classNames({
                                    invalid: invalidEmail,
                                })}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} className="mb-2">
                        <Form.Group
                            controlId={`${prefix}.textMeTheApp.licenseTypes`}
                        >
                            <Select
                                id={`${prefix}.textMeTheApp.licenseTypes`}
                                placeholder="Cert or License Type (select all that apply)"
                                isMulti
                                name="cert"
                                options={licenseTypeOptions}
                                className="cert-multi-select"
                                classNamePrefix="select"
                                onChange={updateLicensesTypes}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            )}
            <Form.Group
                controlId={`${prefix}.textMeTheApp.phoneNumber`}
                className="text-me-the-app-form-submit-container align-items-center mb-2"
                as={Row}
            >
                <Col
                    md={
                        (variant === "stacked" && 12) ||
                        (variant === "extended" && 5) ||
                        4
                    }
                >
                    <Form.Label>Text me the app</Form.Label>
                </Col>
                <Col
                    md={
                        (variant === "stacked" && 12) ||
                        (variant === "extended" && 7) ||
                        8
                    }
                >
                    <Form.Control
                        type="tel"
                        maxLength="14"
                        size="lg"
                        placeholder={
                            variant === "extended"
                                ? "Phone number"
                                : "Enter your phone number"
                        }
                        onChange={updatePhoneNumber}
                        value={phoneNumber}
                        required
                        className={classNames({ invalid: invalidPhoneNo })}
                    />
                    {variant !== "extended" && (
                        <Button
                            variant="link"
                            type="submit"
                            className="text-me-the-app-form-submit"
                            disabled={invalidPhoneNo || submitting}
                        >
                            Send &rarr;
                        </Button>
                    )}
                </Col>
            </Form.Group>
            {variant !== "extended" && (
                <Form.Group
                    controlId={`${prefix}.textMeTheApp.tcpa`}
                    as={Row}
                    className="align-items-start"
                >
                    <Col xs={1}>
                        <Form.Check
                            type="checkbox"
                            size="lg"
                            checked={tcpa}
                            onChange={updateTcpa}
                        />
                    </Col>
                    <Col xs={11}>
                        <Form.Text className="compliance">
                            <DisclaimerCopy />
                        </Form.Text>
                    </Col>
                </Form.Group>
            )}
            {variant === "extended" && (
                <>
                    <Form.Group
                        controlId={`${prefix}.textMeTheApp.tcpa`}
                        as={Row}
                        className="align-items-start mb-2"
                    >
                        <Col xs={2}>
                            <Form.Check
                                type="checkbox"
                                size="lg"
                                checked={tcpa}
                                onChange={updateTcpa}
                            />
                        </Col>
                        <Col xs={11}>
                            <Form.Text className="compliance">
                                <DisclaimerCopy />
                            </Form.Text>
                        </Col>
                    </Form.Group>
                    <Row>
                        <Col md={8}>
                            <div className="mobile-store-buttons">
                                Available for
                                <MobileStoreButton brand="apple" simplified />
                                <MobileStoreButton brand="google" simplified />
                            </div>
                        </Col>
                        <Col md={4}>
                            <Button
                                variant="primary"
                                type="submit"
                                size="lg"
                                disabled={invalidPhoneNo || submitting}
                                className="extended-form-button px-5 py-md-2 ms-md-4"
                            >
                                Send
                            </Button>
                        </Col>
                    </Row>
                </>
            )}
            {submissionError && (
                <Alert variant="danger">{submissionError}</Alert>
            )}
            <InvisibleReCAPTCHA innerRef={reCAPTCHARef} />
            {variant !== "extended" && (
                <div className="mobile-store-buttons mb-4">
                    Available for
                    <MobileStoreButton brand="apple" simplified />
                    <MobileStoreButton brand="google" simplified />
                </div>
            )}
        </Form>
    );
};

TextMeTheAppForm.propTypes = {
    lead: PropTypes.shape({ raw: PropTypes.string }),
    successMessage: PropTypes.shape({ raw: PropTypes.string }),
    className: PropTypes.string,
    prefix: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(["extended", "stacked"]),
};

export default TextMeTheAppForm;
